// src/utils/brokerageSettings.js
// Read/write for the single brokerage-wide settings doc edited from the
// super-admin area: the annual cap and the list of users allowed into that
// area. Mirrors teamDefaults.js.

import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { firestored } from "../firebase/firebaseConfig";

const DOC_PATH = ["settings", "brokerage"];

export const BROKERAGE_SETTINGS_FALLBACK = {
  annualCap: 70000,
  allowedUserIds: [],
};

// Bootstrap owner(s): treated as the super-admin "owner" even without the
// Firestore `superadmin` flag, so the brokerage owner can manage this area
// without a manual database edit. Add/replace emails here if ownership ever
// transfers. Compared case-insensitively.
export const OWNER_EMAILS = ["henry@teamedelstein.com"];

// True ONLY for a bootstrap owner email (OWNER_EMAILS) — the single source of
// truth for super admin. The legacy Firestore `superadmin` flag is no longer
// honored, so a stray flag can never grant super-admin. Owners edit the cap AND
// manage the access list.
export function isSuperAdminOwner(user) {
  if (!user) return false;
  const email = String(user.email || "").toLowerCase();
  return !!email && OWNER_EMAILS.some((e) => e.toLowerCase() === email);
}

// True for anyone allowed into the area — an owner OR a granted guest.
export function canAccessSuperAdmin(user, allowedUserIds = []) {
  if (isSuperAdminOwner(user)) return true;
  return !!(user && user.id) && (allowedUserIds || []).includes(user.id);
}

export async function loadBrokerageSettings() {
  try {
    const ref = doc(firestored, ...DOC_PATH);
    const snap = await getDoc(ref);
    if (!snap.exists()) return { ...BROKERAGE_SETTINGS_FALLBACK };
    const data = snap.data() || {};
    return {
      ...BROKERAGE_SETTINGS_FALLBACK,
      ...data,
      annualCap:
        typeof data.annualCap === "number" && data.annualCap > 0
          ? data.annualCap
          : BROKERAGE_SETTINGS_FALLBACK.annualCap,
      allowedUserIds: Array.isArray(data.allowedUserIds)
        ? data.allowedUserIds
        : [],
    };
  } catch (err) {
    console.error("loadBrokerageSettings failed:", err);
    return { ...BROKERAGE_SETTINGS_FALLBACK };
  }
}

export async function saveBrokerageSettings(values, userId) {
  const ref = doc(firestored, ...DOC_PATH);
  await setDoc(
    ref,
    {
      ...values,
      updatedAt: serverTimestamp(),
      updatedBy: userId || "",
    },
    { merge: true },
  );
}
